import React, {
  createContext,
  ReactNode,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { getUserById } from '../api/user';
import { getSocialMains } from '../api/social';
import { useLocation, useNavigate } from 'react-router-dom';
import { pagesWithSidebar } from '../routes';

// Types
export enum PlatformType {
  INSTAGRAM = 1,
  FACEBOOK = 2,
  METAADS = 3,
  LINKEDIN = 4,
  TIKTOK = 5,
  X_TWITTER = 6,
}

export interface Social {
  id: number;
  provider: number;
  providerId: string;
  providerName: string;
  accessToken: string;
  profilePictureUrl?: string;
  bio: string | null;
  customData?: Record<string, any>;
  profileDescription?: string;
  dmDescription?: string;
  isMain: boolean;
  autoModActive?: boolean;
  autoModInstructions?: string;
  categoryScores?: any;
  autoGenerateCommentsEnabled: boolean;
}

interface User {
  id: number;
  email: string;
  name: string;
  onboarded: boolean;
}

interface SocialContextState {
  currentSocial: Social | null;
  availableSocials: Social[];
  user: User | null;
  isLoading: boolean;
  error: string | null;
}

interface SocialContextType extends SocialContextState {
  switchSocial: (socialId: number) => Promise<void>;
  refreshSocialInfo: () => Promise<void>;
  isSocialAvailable: (socialId: number) => boolean;
}

// Context
const SocialContext = createContext<SocialContextType | undefined>(undefined);

// Provider Component
export const SocialProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [state, setState] = useState<SocialContextState>({
    currentSocial: null,
    availableSocials: [],
    user: null,
    isLoading: true,
    error: null,
  });

  const [socialsLoaded, setSocialsLoaded] = useState(false);
  const [lastUserId, setLastUserId] = useState<number | null>(null);

  const shouldFetchSocials = () => {
    return pagesWithSidebar.some((route) => {
      const regexRoute = new RegExp(`^${route.replace(/:\w+/g, '[^/]+')}/?$`);
      return regexRoute.test(location.pathname);
    });
  };

  const fetchUserAndSocials = async (forceFetchSocials = false) => {
    try {
      console.log(`forcefetchsocials is being forced?`, forceFetchSocials);
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      const userId = Number(localStorage.getItem('userId'));
      if (!userId) {
        throw new Error('User ID not found');
      }

      // Fetch user data
      const user = await getUserById(userId);
      console.log('user check', user);
      if (!user) {
        throw new Error('User not found');
      }
      // Fetch all socials for the user
      if (
        (shouldFetchSocials() && !socialsLoaded) ||
        forceFetchSocials ||
        userId !== lastUserId
      ) {
        const allSocials = await getSocialMains(userId);
        console.log(allSocials);
        if (!allSocials || allSocials.length == 0) {
          if (location.pathname !== '/onboarding') {
            console.log('socialprovider redirect onboarding1');
            navigate('/onboarding');
            return;
          }
        }

        // Get current social from localStorage or default to first available
        let currentSocialId = Number(localStorage.getItem('selectedSocial'));

        const supportedSocials = allSocials.filter(
          (social: Social) => social.provider === 1 || social.provider === 2
        );

        // Find the current social from the available ones
        const currentSocial = currentSocialId
          ? supportedSocials.find(
              (social: Social) => social.id === currentSocialId
            )
          : supportedSocials[0] || null;

        // If the stored current social isn't available, default to the first one
        if (!currentSocial && supportedSocials.length > 0) {
          currentSocialId = supportedSocials[0].id;
          localStorage.setItem('selectedSocial', currentSocialId.toString());
        }

        setState({
          currentSocial,
          availableSocials: allSocials,
          user,
          isLoading: false,
          error: null,
        });

        setSocialsLoaded(true);
        setLastUserId(userId);
      } else {
        setState((prev) => ({
          ...prev,
          user,
          isLoading: false,
          error: null,
        }));
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));

      if (error.message === 'User ID not found') {
        navigate('/login');
      } else if (error.message === 'No social found for the user') {
        console.log('its here in socialProvider');
        navigate('/onboarding');
      }
    }
  };

  const switchSocial = async (socialId: number) => {
    const targetSocial = state.availableSocials.find(
      (social) => social.id === socialId
    );

    if (targetSocial) {
      localStorage.setItem('selectedSocial', socialId.toString());
      setState((prev) => ({ ...prev, currentSocial: targetSocial }));
    }
  };

  useEffect(() => {
    const currentUserId = Number(localStorage.getItem('userId'));

    if (!currentUserId) {
      return;
    }

    const userIdChanged = currentUserId !== lastUserId;

    const needsFetch =
      userIdChanged ||
      (shouldFetchSocials() && !socialsLoaded) ||
      location.pathname === '/postsview';

    if (userIdChanged) {
      setLastUserId(currentUserId);
    }

    // Fetch data if needed
    if (needsFetch) {
      fetchUserAndSocials(true);
    }
  }, [location.pathname, lastUserId]);

  const value: SocialContextType = {
    ...state,
    switchSocial,
    refreshSocialInfo: () => fetchUserAndSocials(true), // Allow manual refresh
    isSocialAvailable: (socialId: number) =>
      state.availableSocials.some((social) => social.id === socialId),
  };

  return (
    <SocialContext.Provider value={value}>{children}</SocialContext.Provider>
  );
};

// Hook
export const useSocial = () => {
  const context = useContext(SocialContext);
  if (!context) {
    throw new Error('useSocial must be used within a SocialProvider');
  }
  return context;
};
